<script setup lang="ts">
import { ref, h, watch } from 'vue'
import { History16Regular } from '@vicons/fluent'
import { type DataTableColumn, NLog } from 'naive-ui'
import type { ImporterJob } from '@/infrastructure/types'
import { useFetch } from '@/plugins/api'
import { formatDateTime } from '@/composables/useFormatDate'
import { intervalToDuration, formatDuration } from 'date-fns'
import ImportStateComponent from './ImportStateComponent.vue'

const props = defineProps<{
  importerId: number
}>()

const showModal = ref(false)
const loading = ref(false)
const period = ref()
const jobs = ref<ImporterJob[]>([])

const columns: DataTableColumn<ImporterJob>[] = [
  {
    title: 'Start time',
    key: 'startTime',
    render: (row) => formatDateTime(row.startTime)
  },
  {
    title: 'Status',
    key: 'importState',
    render: (row) => h(ImportStateComponent, { state: row.importState })
  },
  {
    title: 'Progress',
    key: 'progressInPercent',
    align: 'right',
    render: (row) => row.progressInPercent + '%'
  },
  {
    title: 'Duration',
    key: 'durationInSeconds',
    align: 'right',
    render: (row) => {
      return row.durationInSeconds ? formatDuration(intervalToDuration({ start: 0, end: row.durationInSeconds * 1000 })) : '0 seconds'
    }
  },
  {
    type: 'expand',
    renderExpand: (row) => h(NLog, { log: row.message, fontSize: 12 })
  }
]

watch(period, (value) => {
  if (Array.isArray(value)) {
    const query = {
      fromDate: new Date(value[0]).toISOString(),
      toDate: new Date(value[1]).toISOString()
    }

    fetchData(query)
  } else {
    fetchData()
  }
})

async function fetchData(query?: object) {
  loading.value = true
  jobs.value = await useFetch(`importers/${props.importerId}/history`, query)
  loading.value = false
}
</script>

<template>
  <n-button :render-icon="() => h(History16Regular)" @click="showModal = true">
    Show history
  </n-button>

  <n-modal
    v-model:show="showModal"
    style="max-width: 800px"
    :on-after-enter="fetchData"
    :on-after-leave="() => jobs = []"
  >
    <n-card title="History">
      <template #header-extra>
        <n-form-item label="Period" label-placement="left" :show-feedback="false">
          <cdi-date-picker
            v-model:formatted-value="period"
            type="daterange"
            close-on-select
            :on-confirm="fetchData"
          ></cdi-date-picker>
        </n-form-item>
      </template>

      <div class="mb-2">
        <n-collapse-transition :show="!period">
          <n-text depth="3">
            No period selected - Showing latest runs
          </n-text>
        </n-collapse-transition>
      </div>

      <n-data-table
        :columns="columns"
        :data="jobs"
        :loading="loading"
        :row-key="(row: ImporterJob) => new Date(row.startTime)"
      ></n-data-table>
    </n-card>
  </n-modal>
</template>